/**
 * Checks whether the passed value contains letters.
 * Used to detect placeholders or masked input characters that should not be parsed as dates.
 * @param {string} value - The value to inspect.
 * @return {boolean} - Returns `true` if the value contains letters, otherwise `false`.
 * @example
 *    containsLetters('TT.MM.JJJJ') → true
 *    containsLetters('01.01.1990') → false
 */
export const containsLetters = (value) => /[a-zA-Z]/.test(value);
/**
 * Normalize supported input separators to a dot so different input formats
 * can be parsed in a unified way.
 * @param {string} value - The raw input value.
 * @return {string} - Returns the normalized input value.
 * @example
 *    normalizeInputSeparators('01-01-1990') → '01.01.1990'
 *    normalizeInputSeparators('1990/01/01') → '1990.01.01'
 */
export const normalizeInputSeparators = (value) => value.trim().replace(/[\/\-.]/g, '.');
/**
 * Safely construct a date object from numeric date parts.
 * Unlike `new Date(string)` this also validates that the resulting date
 * did not overflow, e.g. `31.02.2020`.
 * @param {ParsedDateParts} param0 - The parsed date parts.
 * @param {number} param0.year - The full year.
 * @param {number} param0.month - The month number from `1` to `12`.
 * @param {number} param0.day - The day number from `1` to `31`.
 * @return {Date|null} - Returns a valid `Date` object or `null` if invalid.
 * @example
 *    buildStrictDate({ year: 1990, month: 1, day: 1 }) → Date
 *    buildStrictDate({ year: 2020, month: 2, day: 31 }) → null
 */
export const buildStrictDate = ({ year, month, day }) => {
    /**
     * Ensure all date parts are integers.
     */
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
        return null;
    }
    /**
     * Apply a basic date range validation before constructing the date object.
     */
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return null;
    }
    const parsed = new Date(year, month - 1, day);
    /**
     * Ensure the resulting date matches the originally passed date parts.
     * This prevents overflow dates such as `31.02.2020` from being accepted.
     */
    if (parsed.getFullYear() !== year || parsed.getMonth() !== month - 1 || parsed.getDate() !== day) {
        return null;
    }
    return parsed;
};
/**
 * Parse a date string against a token-based date format.
 * The input value and the format are normalized so separators like `.`, `-` and `/`
 * can be handled consistently.
 * @param {string} value - The raw input value to parse.
 * @param {string} format - The token-based date format, e.g. `d.m.Y` or `Y-m-d`.
 * @return {Date|null} - Returns a valid `Date` object or `null` if parsing fails.
 * @example
 *    parseByFormatTokens('01-01-1990', 'd.m.Y') → Date
 *    parseByFormatTokens('1990/01/01', 'Y-m-d') → Date
 *    parseByFormatTokens('31.02.2020', 'd.m.Y') → null
 */
export const parseByFormatTokens = (value, format) => {
    const normalizedValue = normalizeInputSeparators(value);
    const normalizedFormat = normalizeInputSeparators(format);
    const formatParts = normalizedFormat.split('.');
    const valueParts = normalizedValue.split('.');
    /**
     * Bail out if the number of tokens does not match.
     */
    if (formatParts.length !== valueParts.length) {
        return null;
    }
    const tokenMap = {};
    for (let i = 0; i < formatParts.length; i++) {
        const token = formatParts[i];
        const rawValuePart = valueParts[i];
        /**
         * All value parts must be numeric.
         */
        if (!/^\d+$/.test(rawValuePart)) {
            return null;
        }
        const numericValue = Number(rawValuePart);
        /**
         * Assign the numeric value to its matching date token.
         */
        if (token === 'Y') {
            if (rawValuePart.length !== 4)
                return null;
            tokenMap.year = numericValue;
        }
        else if (token === 'm') {
            if (rawValuePart.length < 1 || rawValuePart.length > 2)
                return null;
            tokenMap.month = numericValue;
        }
        else if (token === 'd') {
            if (rawValuePart.length < 1 || rawValuePart.length > 2)
                return null;
            tokenMap.day = numericValue;
        }
        else {
            /**
             * Unknown format token.
             */
            return null;
        }
    }
    /**
     * Ensure all required date parts were extracted successfully.
     */
    if (typeof tokenMap.year !== 'number' ||
        typeof tokenMap.month !== 'number' ||
        typeof tokenMap.day !== 'number') {
        return null;
    }
    return buildStrictDate({
        year: tokenMap.year,
        month: tokenMap.month,
        day: tokenMap.day,
    });
};
/**
 * Get the configured date format plus supported alternative input formats.
 * This allows the picker to be more tolerant when handling pasted or autofilled values.
 * @param {string} dateFormat - The configured date format of the picker.
 * @return {string[]} - Returns a de-duplicated list of accepted input formats.
 * @example
 *    getAcceptedInputFormats('d.m.Y') → ['d.m.Y', 'Y.m.d', 'm.d.Y']
 */
export const getAcceptedInputFormats = (dateFormat) => {
    const normalizedConfiguredFormat = normalizeInputSeparators(dateFormat);
    /**
     * Supported fallback formats for tolerant parsing of pasted or autofilled dates.
     */
    const fallbackFormats = ['Y.m.d', 'd.m.Y', 'm.d.Y'];
    return Array.from(new Set([normalizedConfiguredFormat, ...fallbackFormats]));
};
